import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Tooltip } from "@ondit/ui";
import { ChartColumn, Check, Sparkles } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { useT } from "~/core/i18n";
import { type Module, moduleHome, useModule } from "~/shell/module";

const HALVES: { module: Module; label: "nav.analysisMode" | "nav.agentMode"; icon: ReactNode }[] = [
	{ module: "agent", label: "nav.agentMode", icon: <Sparkles aria-hidden /> },
	// Not the gauge: that is the Overview row, and collapsed the two sit one
	// above the other in the rail — a glyph for a module and a glyph for one of
	// its screens, drawn identically.
	{ module: "analytics", label: "nav.analysisMode", icon: <ChartColumn aria-hidden /> },
];

/**
 * Analytics and Agents, as one segmented control rather than a link and a chip.
 *
 * These are the two halves of the same product — what it found and what it does
 * — so they are drawn as one switch with two equal halves. Both halves are
 * router links now: they were a link and a hard `window.location.assign`
 * because the Agent used to be a second application behind a reverse proxy, and
 * crossing between them cost a full document load and whatever was in the
 * composer.
 *
 * The track uses the content background so the selected surface stays distinct
 * from both its track and the sidebar in either theme.
 *
 * Collapsed, it is one glyph — the module you are in — that opens the pair as a
 * menu. It rendered nothing at all there to begin with, which left a reader who
 * had narrowed the column with no way across short of widening it again; then
 * it was both glyphs stacked, which spent two of the rail's dozen rows saying
 * what one of them already said. A rail row is a thing you are on or a thing
 * you open, and this is the second.
 */
export function ModeSwitcher({ brandId, collapsed = false }: { brandId: string; collapsed?: boolean }) {
	const t = useT();
	// Which half is lit is a question about the whole path, and on a screen that
	// is neither module's it is a question the path cannot answer — see
	// `~/shell/module`.
	const active = useModule();

	// The panel runs without Tailwind's preflight, so a link arrives with the
	// browser's underline; the component library's stylesheet takes it off,
	// which is why the same switch read as two underlined links in one module
	// and as tabs in the other.
	const navigate = useNavigate();
	const base = "rounded-control no-underline transition-colors";
	const half = `${base} flex-1 px-2 py-1 text-center text-label`;

	if (collapsed) {
		const here = HALVES.find((one) => one.module === active) ?? HALVES[0];
		return (
			<Tooltip.Provider delayDuration={0}>
				<div className="mb-2 flex justify-center">
					<DropdownMenu>
						<Tooltip.Root>
							<Tooltip.Trigger asChild>
								<DropdownMenuTrigger
									className="flex size-8 items-center justify-center rounded-control text-[var(--fg-3)] transition-colors hover:bg-[var(--surface-hover)] hover:text-fg [&_svg]:size-4"
									aria-label={`${t("nav.mode")}: ${t(here.label)}`}
								>
									{here.icon}
								</DropdownMenuTrigger>
							</Tooltip.Trigger>
							<Tooltip.Content side="right">{t(here.label)}</Tooltip.Content>
						</Tooltip.Root>
						<DropdownMenuContent align="start" side="right">
							{HALVES.map(({ module, label, icon }) => (
								<DropdownMenuItem key={module} onSelect={() => navigate(moduleHome(brandId, module))}>
									{icon}
									<span className="min-w-0 flex-1">{t(label)}</span>
									{module === active ? <Check /> : null}
								</DropdownMenuItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</Tooltip.Provider>
		);
	}

	return (
		<Tooltip.Provider delayDuration={0}>
			<div
				className="mb-2 flex items-center gap-(--s-1) rounded-control border border-[var(--border)] bg-[var(--bg)] p-(--s-1)"
				role="tablist"
				aria-label={t("nav.mode")}
			>
				{HALVES.map(({ module, label }) => {
					const chosen = module === active;
					return (
						/*
						 * `aria-selected` rather than the `aria-current` a NavLink emits: a
						 * tablist promises its tabs say which is selected, and "this is the
						 * page you are on" is a different sentence a screen reader reads out
						 * differently — and on a common screen it would be a false one.
						 */
						<Link
							key={module}
							to={moduleHome(brandId, module)}
							role="tab"
							aria-selected={chosen}
							className={`${half} ${chosen ? "bg-[var(--surface-active)] font-medium text-fg" : "text-[var(--fg-3)] hover:bg-[var(--surface-hover)] hover:text-fg"}`}
						>
							{t(label)}
						</Link>
					);
				})}
			</div>
		</Tooltip.Provider>
	);
}
